import { c as _c } from "react/compiler-runtime";
import type { ChallengeKind } from '@onefootprint/request-types';
import { Button, RadioSelect } from '@onefootprint/ui';
import type { FormEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isBiometric } from '@/idv/utils';
import { IdentifyVariant } from '../../../identify.types';
import getHeader from '../../../utils/get-header';
import Challenge from '../../challenge';
import useRunPasskey from '../hooks/run-passkey';
import type { LoginChallengeProps } from '../login-challenge';
import { getChallengeTitleByKind, getMethods } from './utils';
enum Screen {
  challengeSelector = 'challengeSelector',
  challenge = 'challenge',
}
type ChallengeSelectOrPasskeyProps = Omit<LoginChallengeProps, 'onReset'> & {
  children: React.ReactNode;
};
const ChallengeSelectOrPasskey = t0 => {
  const $ = _c(61);
  const {
    context,
    requirement,
    onPrev,
    onDone,
    children
  } = t0;
  const {
    initArgs: t1,
    state: t2,
    onError
  } = context;
  const {
    device,
    logoConfig,
    variant
  } = t1;
  const {
    phoneNumber,
    email,
    identifyToken
  } = t2;
  const {
    t
  } = useTranslation("identify");
  const [selectedChallenge, setSelectedChallenge] = useState(undefined);
  const [screen, setScreen] = useState(Screen.challengeSelector);
  let t3;
  if ($[0] !== onDone) {
    t3 = {
      onDone
    };
    $[0] = onDone;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const runPasskey = useRunPasskey(t3);
  let t4;
  if ($[2] !== device || $[3] !== email || $[4] !== phoneNumber || $[5] !== requirement.user || $[6] !== t) {
    const ctx = {
      user: requirement.user,
      phoneNumber,
      email
    };
    const titleMap = getChallengeTitleByKind(t, ctx);
    t4 = getMethods(requirement.user, device, titleMap);
    $[2] = device;
    $[3] = email;
    $[4] = phoneNumber;
    $[5] = requirement.user;
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const methodOptions = t4;
  let t5;
  if ($[8] !== methodOptions[0] || $[9] !== methodOptions.length || $[10] !== selectedChallenge) {
    t5 = () => {
      if (methodOptions.length && !selectedChallenge) {
        setSelectedChallenge(methodOptions[0].value);
      }
    };
    $[8] = methodOptions[0];
    $[9] = methodOptions.length;
    $[10] = selectedChallenge;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== methodOptions || $[13] !== selectedChallenge) {
    t6 = [selectedChallenge, methodOptions];
    $[12] = methodOptions;
    $[13] = selectedChallenge;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  useEffect(t5, t6);
  let t7;
  if ($[15] !== identifyToken || $[16] !== runPasskey || $[17] !== selectedChallenge) {
    t7 = ev => {
      ev.preventDefault();
      if (!selectedChallenge) {
        return;
      }
      if (isBiometric(selectedChallenge)) {
        runPasskey.mutate(identifyToken);
      } else {
        setScreen(Screen.challenge);
      }
    };
    $[15] = identifyToken;
    $[16] = runPasskey;
    $[17] = selectedChallenge;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const handleSubmit = t7;
  if (screen === Screen.challenge && selectedChallenge) {
    const t8 = email?.value;
    const t9 = phoneNumber?.value;
    let t10;
    if ($[19] !== t8 || $[20] !== t9) {
      t10 = {
        email: t8,
        phoneNumber: t9
      };
      $[19] = t8;
      $[20] = t9;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    let t11;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = () => setScreen(Screen.challengeSelector);
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    let t12;
    if ($[23] !== context || $[24] !== onDone || $[25] !== onError || $[26] !== requirement.user || $[27] !== selectedChallenge || $[28] !== t10) {
      t12 = <Challenge defaultValues={t10} context={context} onPrev={t11} onDone={onDone} onError={onError} user={requirement.user} challengeKind={selectedChallenge} fromChallengeSelector={true} />;
      $[23] = context;
      $[24] = onDone;
      $[25] = onError;
      $[26] = requirement.user;
      $[27] = selectedChallenge;
      $[28] = t10;
      $[29] = t12;
    } else {
      t12 = $[29];
    }
    return t12;
  }
  let t8;
  if ($[30] !== logoConfig || $[31] !== onPrev) {
    t8 = getHeader(logoConfig, onPrev);
    $[30] = logoConfig;
    $[31] = onPrev;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const Header = t8;
  let t9;
  if ($[33] !== t || $[34] !== variant) {
    t9 = variant !== IdentifyVariant.updateLoginMethods ? t(_temp) : t(_temp2);
    $[33] = t;
    $[34] = variant;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  const headerSubtitle = t9;
  const shouldShowWelcomeBack = !requirement.user.isUnverified;
  let t10;
  if ($[36] !== shouldShowWelcomeBack || $[37] !== t) {
    t10 = shouldShowWelcomeBack ? t(_temp3) : t(_temp4);
    $[36] = shouldShowWelcomeBack;
    $[37] = t;
    $[38] = t10;
  } else {
    t10 = $[38];
  }
  const title = t10;
  let t11;
  if ($[39] !== Header || $[40] !== headerSubtitle || $[41] !== title) {
    t11 = <Header subtitle={headerSubtitle} title={title} />;
    $[39] = Header;
    $[40] = headerSubtitle;
    $[41] = title;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  let t12;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = value => setSelectedChallenge(value as ChallengeKind);
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  const t13 = selectedChallenge || "";
  let t14;
  if ($[44] !== methodOptions || $[45] !== t13) {
    t14 = <RadioSelect onChange={t12} options={methodOptions} size="compact" value={t13} />;
    $[44] = methodOptions;
    $[45] = t13;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  const t15 = !selectedChallenge;
  const t16 = runPasskey.isPending;
  let t17;
  if ($[47] !== t) {
    t17 = t(_temp5);
    $[47] = t;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== runPasskey.isPending || $[50] !== t15 || $[51] !== t17) {
    t18 = <Button disabled={t15} fullWidth={true} loading={t16} type="submit" size="large" data-dd-action-name="challenge:continue">{t17}</Button>;
    $[49] = runPasskey.isPending;
    $[50] = t15;
    $[51] = t17;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] !== children || $[54] !== t11 || $[55] !== t14 || $[56] !== t18) {
    t19 = <div className="flex flex-col gap-5">{t11}{t14}{t18}{children}</div>;
    $[53] = children;
    $[54] = t11;
    $[55] = t14;
    $[56] = t18;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] !== handleSubmit || $[59] !== t19) {
    t20 = <form onSubmit={handleSubmit}>{t19}</form>;
    $[58] = handleSubmit;
    $[59] = t19;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  return t20;
};
export default ChallengeSelectOrPasskey;
function _temp($) {
  return $["challenge-select-or-biometric"]["log-in-options"];
}
function _temp2($_0) {
  return $_0["challenge-select-or-biometric"]["log-in-to-modify-details"];
}
function _temp3($_1) {
  return $_1["challenge-select-or-biometric"]["welcome-back-title"];
}
function _temp4($_2) {
  return $_2["challenge-select-or-biometric"]["verify-title"];
}
function _temp5($_3) {
  return $_3.continue;
}